import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./UserAdminModal.module.css";
import ConfirmDialog from "./ConfirmDialog";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import { useToast } from "./ToastProvider";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useHistoryBackClose } from "../hooks/useHistoryBackClose";
import InviteStudentModal from "./InviteStudentModal";
import { isPasswordStrong } from "./PasswordRequirementsChecklist";
import { UserAdminContext } from "../context/UserAdminContext";
import UsersListTab from "./userAdmin/UsersListTab";
import UserFormTab from "./userAdmin/UserFormTab";
import MyProfileTab from "./userAdmin/MyProfileTab";
import {
  getEmptyUserForm,
  getEmptyPasswordForm,
  buildImageSrc,
  getUserLabel,
  canProfessorManageStudent,
} from "./userAdmin/userAdminHelpers";

const USERS_PAGE_SIZE = 100;

export default function UserAdminModal({ open, onClose, apiBase = "", token, onUpdated }) {
  useHistoryBackClose(open, onClose);
  // Ref pra sempre chamar a versão mais recente de onUpdated sem colocar essa
  // função (instável - recriada a cada render do pai) nas deps do refreshAll,
  // o que causava um loop infinito de fetch (useEffect -> refreshAll ->
  // onUpdated -> setMe no pai -> re-render -> nova função -> useEffect de novo).
  const onUpdatedRef = useRef(onUpdated);
  useEffect(() => { onUpdatedRef.current = onUpdated; }, [onUpdated]);
  useBodyScrollLock();
  const [tab, setTab] = useState("list");
  const [showInvite, setShowInvite] = useState(false);
  const toast = useToast();
  const [editingUser, setEditingUser] = useState(null);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [users, setUsers] = useState([]);
  // Paginação da lista (o backend devolve 100 por página): sem isso, academia
  // com mais de 100 alunos via só os primeiros, sem aviso nenhum.
  const [usersPage, setUsersPage] = useState({ hasMore: false, total: 0, q: "" });
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  const [form, setForm] = useState(getEmptyUserForm());
  const [passwordForm, setPasswordForm] = useState(getEmptyPasswordForm());
  const [adminPwForm, setAdminPwForm] = useState({ new_password: "", confirm_password: "" });

  const [profileForm, setProfileForm] = useState({
    name: "",
    photo: null,
    preview: "",
    brand_color: "",
    brand_bg_color_dark: "",
    brand_bg_color_light: "",
  });

  // ── Logo personalizado do professor ───────────────────────────────────────
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoSaving, setLogoSaving] = useState(false);

  // ── Variante clara do logo (opcional, R248) — mesmo padrão 3-estados do
  // logo acima, espelhado pro slot "light". Trocada pelo mesmo mecanismo
  // dark/light do wordmark padrão (ModalHeader.jsx); sem ela, o logo "dark"
  // acima continua valendo pros dois modos, sem nenhuma mudança de
  // comportamento pra quem não usar isso.
  const [logoLightFile, setLogoLightFile] = useState(null);
  const [logoLightPreview, setLogoLightPreview] = useState("");
  const [logoLightSaving, setLogoLightSaving] = useState(false);

  // ── Ícone do app (PWA) do professor — separado do logo, ver docs/07-issues.md ──
  const [appIconFile, setAppIconFile] = useState(null);
  const [appIconPreview, setAppIconPreview] = useState("");
  const [appIconSaving, setAppIconSaving] = useState(false);

  function resetFeedback() {
    setError("");
    setSuccess("");
  }

  // useCallback (nao function declaration): entra nas deps do useMemo de
  // userAdminContextValue mais abaixo -- sem isso, identidade nova a cada
  // render invalida a memoizacao (aviso eslint react-hooks/exhaustive-deps).
  const resetUserForm = useCallback(() => {
    setEditingUser(null);
    setForm(getEmptyUserForm());
    setAdminPwForm({ new_password: "", confirm_password: "" });
  }, []);

  function resetPasswordForm() {
    setPasswordForm(getEmptyPasswordForm());
  }

  const loadMe = useCallback(async () => {
    const res = await api.get("/users/me");
    return res.data;
  }, []);

  // scope "org": professor de academia recebe a carteira da academia inteira
  // (rodízio). Professor autônomo recebe só os dele (o backend trata "org"
  // como "mine" sem academia) e admin ignora o parâmetro.
  const loadUsers = useCallback(async (q, offset = 0) => {
    const params = { scope: "org", limit: USERS_PAGE_SIZE, offset };
    if (q) params.q = q;
    const res = await api.get("/users", { params });
    const total = parseInt(res.headers?.["x-total-count"] ?? "", 10);
    setUsersPage({
      hasMore: String(res.headers?.["x-has-more"] ?? "") === "true",
      total: Number.isFinite(total) ? total : offset + res.data.length,
      q: q || "",
    });
    return res.data;
  }, []);

  const loadMoreUsers = useCallback(async () => {
    setLoadingMoreUsers(true);
    try {
      const more = await loadUsers(usersPage.q, users.length);
      setUsers((prev) => [...prev, ...more]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível carregar mais usuários.");
    } finally {
      setLoadingMoreUsers(false);
    }
  }, [loadUsers, usersPage.q, users.length, toast]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const meData = await loadMe();
      const usersData = await loadUsers();

      setCurrentUser(meData);
      setUsers(usersData);
      // Mantém o Dashboard/App sincronizados com nome/avatar/tema, senão a
      // marca (cor/logo) recém-salva só aparece após reload da página.
      onUpdatedRef.current?.(meData);

      setProfileForm({
        name: meData?.name || "",
        photo: null,
        preview: meData?.avatar_url ? buildImageSrc(meData.avatar_url, apiBase) : "",
        brand_color: meData?.own_brand_color || "",
        brand_bg_color_dark: meData?.own_bg_color_dark || "",
        brand_bg_color_light: meData?.own_bg_color_light || "",
      });

      // Logo próprio, só relevante quando o usuário logado é professor
      setLogoFile(null);
      setLogoPreview(
        meData?.role === "professor" && meData?.custom_logo_url
          ? buildImageSrc(meData.custom_logo_url, apiBase)
          : ""
      );
      setLogoLightFile(null);
      setLogoLightPreview(
        meData?.role === "professor" && meData?.custom_logo_light_url
          ? buildImageSrc(meData.custom_logo_light_url, apiBase)
          : ""
      );
      setAppIconFile(null);
      setAppIconPreview(
        meData?.role === "professor" && meData?.custom_app_icon_url
          ? buildImageSrc(meData.custom_app_icon_url, apiBase)
          : ""
      );

      if (meData?.role === "aluno" || meData?.role === "google") {
        setTab("profile");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Erro ao carregar usuários";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [loadMe, loadUsers, apiBase]);

  useEffect(() => {
    if (!open) return;
    refreshAll();
  }, [open, refreshAll]);

  useEffect(() => {
    if (!open) {
      setUsers([]);
      setCurrentUser(null);
      setEditingUser(null);
      setLoading(false);
      setSaving(false);
      setError("");
      setSuccess("");
      setForm(getEmptyUserForm());
      setPasswordForm(getEmptyPasswordForm());
      setProfileForm({
        name: "",
        photo: null,
        preview: "",
        brand_color: "",
      });
      setLogoFile(null);
      setLogoPreview("");
      setAppIconFile(null);
      setAppIconPreview("");
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (profileForm.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(profileForm.preview);
      }
    };
  }, [profileForm.preview]);

  useEffect(() => {
    if (!open) return;

    function onKeyDown(e) {
      if (e.key === "Escape") onClose();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const modalDescription = useMemo(() => {
    if (currentUser?.role === "admin") return "Gerencie administradores, professores e alunos";
    if (currentUser?.role === "professor") return "Gerencie seus alunos";
    if (currentUser?.role === "aluno" || currentUser?.role === "google") return "Visualize e atualize seus dados";
    return "Carregando permissões do usuário";
  }, [currentUser?.role]);

  const allowedCreateRoles = useMemo(() => {
    if (currentUser?.role === "admin") return ["admin", "professor", "aluno"];
    if (currentUser?.role === "professor") return ["aluno"];
    return [];
  }, [currentUser?.role]);

  const professors = useMemo(() => {
    return users.filter((u) => u.role === "professor");
  }, [users]);

  const visibleUsers = useMemo(() => {
    if (currentUser?.role === "admin") return users;

    if (currentUser?.role === "professor") {
      return users.filter((u) => canProfessorManageStudent(currentUser, u));
    }

    return users.filter((u) => u.id === currentUser?.id);
  }, [users, currentUser]);

  // Busca server-side com debounce: a lista de usuários é paginada no backend
  // (débito 009), então filtrar só no cliente veria apenas a 1ª página. Admin
  // vê a base inteira — aqui o `?q=` faz o servidor filtrar por nome/email.
  const didInitSearchRef = useRef(false);
  useEffect(() => {
    if (!open || tab !== "list") return;
    const termo = searchQuery.trim();
    // O 1º carregamento (termo vazio) já foi feito por refreshAll no open —
    // pular aqui evita um fetch duplicado. Buscas subsequentes rodam normal.
    if (!didInitSearchRef.current && termo === "") {
      didInitSearchRef.current = true;
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const data = await loadUsers(termo);
        setUsers(data);
      } catch (_) {
        // silencioso: mantém a lista atual em caso de erro de rede
      }
    }, termo ? 300 : 0);
    return () => clearTimeout(handle);
  }, [searchQuery, open, tab, loadUsers]);

  // Reseta o guard quando o modal fecha, pra o próximo open não refetchar à toa.
  useEffect(() => {
    if (!open) didInitSearchRef.current = false;
  }, [open]);

  const userLabels = getUserLabel(currentUser?.role);


  const runAction = useCallback(
    async (action, successMessage) => {
      resetFeedback();
      setSaving(true);

      try {
        await action();
        if (successMessage) setSuccess(successMessage);
        await refreshAll();
        return true;
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Ocorreu um erro";
        setError(msg);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [refreshAll]
  );

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const startCreate = useCallback(() => {
    resetFeedback();
    resetUserForm();
    setTab("form");
  }, [resetUserForm]);

  const startEdit = useCallback((user) => {
    resetFeedback();
    setEditingUser(user);
    setForm({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: user.role || "aluno",
      professor_id: user.professor_id || "",
      weekly_goal: String(user.weekly_goal ?? "3"),
      is_active: user.is_active ?? true,
      plan_expires_at: user.plan_expires_at || "",
      brand_color: user.brand_color || "",
      brand_bg_color_dark: user.brand_bg_color_dark || "",
      brand_bg_color_light: user.brand_bg_color_light || "",
      is_self_service: user.is_self_service ?? false,
      student_billing_override: user.student_billing_override ?? false,
    });
    // Carrega logo/ícone existente do professor (se houver)
    setLogoFile(null);
    setLogoPreview(
      user.role === "professor" && user.custom_logo_url
        ? buildImageSrc(user.custom_logo_url, apiBase)
        : ""
    );
    setLogoLightFile(null);
    setLogoLightPreview(
      user.role === "professor" && user.custom_logo_light_url
        ? buildImageSrc(user.custom_logo_light_url, apiBase)
        : ""
    );
    setAppIconFile(null);
    setAppIconPreview(
      user.role === "professor" && user.custom_app_icon_url
        ? buildImageSrc(user.custom_app_icon_url, apiBase)
        : ""
    );
    setTab("form");
  }, [apiBase]);

  const handleLogoFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  // Alvo do logo: usuário sendo editado pelo admin, ou o próprio usuário
  // logado (professor autogerenciando sua marca em "Meu perfil").
  const logoTargetId = editingUser?.id ?? currentUser?.id;

  const handleLogoUpload = useCallback(async () => {
    if (!logoFile || !logoTargetId) return;
    setLogoSaving(true);
    try {
      const fd = new FormData();
      fd.append("logo", logoFile);
      const res = await api.post(`/users/${logoTargetId}/logo`, fd);
      const newUrl = res.data.custom_logo_url;
      if (editingUser) {
        setEditingUser((prev) => prev ? { ...prev, custom_logo_url: newUrl } : prev);
        setUsers((prev) =>
          prev.map((u) => u.id === logoTargetId ? { ...u, custom_logo_url: newUrl } : u)
        );
      } else {
        setCurrentUser((prev) => prev ? { ...prev, custom_logo_url: newUrl } : prev);
      }
      setLogoFile(null);
      setSuccess("Logo salvo com sucesso.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao salvar logo.");
    } finally {
      setLogoSaving(false);
    }
  }, [logoFile, logoTargetId, editingUser]);

  const handleLogoRemove = useCallback(async () => {
    if (!logoTargetId) return;
    setLogoSaving(true);
    try {
      await api.delete(`/users/${logoTargetId}/logo`);
      if (editingUser) {
        setEditingUser((prev) => prev ? { ...prev, custom_logo_url: null } : prev);
        setUsers((prev) =>
          prev.map((u) => u.id === logoTargetId ? { ...u, custom_logo_url: null } : u)
        );
      } else {
        setCurrentUser((prev) => prev ? { ...prev, custom_logo_url: null } : prev);
      }
      setLogoFile(null);
      setLogoPreview("");
      setSuccess("Logo removido.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao remover logo.");
    } finally {
      setLogoSaving(false);
    }
  }, [logoTargetId, editingUser]);

  // ── Variante clara do logo (opcional, R248) — mesmo trio de handlers
  // acima, só trocando o campo "variant" enviado ao backend e o campo
  // (custom_logo_light_url) atualizado no estado local.
  const handleLogoLightFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoLightFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoLightPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleLogoLightUpload = useCallback(async () => {
    if (!logoLightFile || !logoTargetId) return;
    setLogoLightSaving(true);
    try {
      const fd = new FormData();
      fd.append("logo", logoLightFile);
      fd.append("variant", "light");
      const res = await api.post(`/users/${logoTargetId}/logo`, fd);
      const newUrl = res.data.custom_logo_light_url;
      if (editingUser) {
        setEditingUser((prev) => prev ? { ...prev, custom_logo_light_url: newUrl } : prev);
        setUsers((prev) =>
          prev.map((u) => u.id === logoTargetId ? { ...u, custom_logo_light_url: newUrl } : u)
        );
      } else {
        setCurrentUser((prev) => prev ? { ...prev, custom_logo_light_url: newUrl } : prev);
      }
      setLogoLightFile(null);
      setSuccess("Logo (variante clara) salvo com sucesso.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao salvar logo (variante clara).");
    } finally {
      setLogoLightSaving(false);
    }
  }, [logoLightFile, logoTargetId, editingUser]);

  const handleLogoLightRemove = useCallback(async () => {
    if (!logoTargetId) return;
    setLogoLightSaving(true);
    try {
      await api.delete(`/users/${logoTargetId}/logo`, { params: { variant: "light" } });
      if (editingUser) {
        setEditingUser((prev) => prev ? { ...prev, custom_logo_light_url: null } : prev);
        setUsers((prev) =>
          prev.map((u) => u.id === logoTargetId ? { ...u, custom_logo_light_url: null } : u)
        );
      } else {
        setCurrentUser((prev) => prev ? { ...prev, custom_logo_light_url: null } : prev);
      }
      setLogoLightFile(null);
      setLogoLightPreview("");
      setSuccess("Logo (variante clara) removido.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao remover logo (variante clara).");
    } finally {
      setLogoLightSaving(false);
    }
  }, [logoTargetId, editingUser]);

  const handleAppIconFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAppIconFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAppIconPreview(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleAppIconUpload = useCallback(async () => {
    if (!appIconFile || !logoTargetId) return;
    setAppIconSaving(true);
    try {
      const fd = new FormData();
      fd.append("icon", appIconFile);
      const res = await api.post(`/users/${logoTargetId}/app-icon`, fd);
      const newUrl = res.data.custom_app_icon_url;
      if (editingUser) {
        setEditingUser((prev) => prev ? { ...prev, custom_app_icon_url: newUrl } : prev);
        setUsers((prev) =>
          prev.map((u) => u.id === logoTargetId ? { ...u, custom_app_icon_url: newUrl } : u)
        );
      } else {
        setCurrentUser((prev) => prev ? { ...prev, custom_app_icon_url: newUrl } : prev);
      }
      setAppIconFile(null);
      setSuccess("Ícone do app salvo com sucesso.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao salvar ícone do app.");
    } finally {
      setAppIconSaving(false);
    }
  }, [appIconFile, logoTargetId, editingUser]);

  const handleAppIconRemove = useCallback(async () => {
    if (!logoTargetId) return;
    setAppIconSaving(true);
    try {
      await api.delete(`/users/${logoTargetId}/app-icon`);
      if (editingUser) {
        setEditingUser((prev) => prev ? { ...prev, custom_app_icon_url: null } : prev);
        setUsers((prev) =>
          prev.map((u) => u.id === logoTargetId ? { ...u, custom_app_icon_url: null } : u)
        );
      } else {
        setCurrentUser((prev) => prev ? { ...prev, custom_app_icon_url: null } : prev);
      }
      setAppIconFile(null);
      setAppIconPreview("");
      setSuccess("Ícone do app removido.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao remover ícone do app.");
    } finally {
      setAppIconSaving(false);
    }
  }, [logoTargetId, editingUser]);

  const handleCreateUser = useCallback(
    async (payload) => {
      await api.post("/users", payload);
    },
    []
  );

  const handleUpdateUser = useCallback(
    async (userId, payload) => {
      await api.put(`/users/${userId}`, payload);
    },
    []
  );

  const handleDeleteUser = useCallback(
    async (userId) => {
      await api.delete(`/users/${userId}`);
    },
    []
  );

  const handleUpdateOwnProfile = useCallback(
    async (formData) => {
      await api.put("/users/me/profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    []
  );

  const handleChangeMyPassword = useCallback(
    async (payload) => {
      await api.put("/users/me/password", payload);
    },
    []
  );

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmLabel, setConfirmLabel] = useState("Excluir");
  const confirmActionRef = useRef(null);

  const onClickDelete = useCallback(
    async (userId) => {
      setConfirmMessage("Deseja realmente excluir este usuário?");
      setConfirmLabel("Excluir");
      confirmActionRef.current = async () => {
        await runAction(() => handleDeleteUser(userId), "Usuário excluído com sucesso.");
      };
      setShowConfirm(true);
    },
    [runAction, handleDeleteUser]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      resetFeedback();

      if (!currentUser) return;

      if (editingUser) {
        const payload = {
          name: form.name,
          email: form.email,
        };

        if (currentUser.role === "admin") {
          payload.role = form.role;
          payload.professor_id =
            form.role === "aluno" && form.professor_id !== ""
              ? Number(form.professor_id)
              : null;

          if (form.weekly_goal !== "") {
            const n = Number(form.weekly_goal);
            if (!Number.isNaN(n)) payload.weekly_goal = n;
          }

          payload.is_active = form.is_active;
          payload.plan_expires_at = form.plan_expires_at || null;
          payload.is_self_service = !!form.is_self_service;
          // Só tem efeito quando professor não é self-service (não paga a
          // plataforma) — libera a gestão de pagamento dos alunos sem exigir
          // assinatura da plataforma (degustação parcial).
          if (form.role === "professor" && !form.is_self_service) {
            payload.student_billing_override = !!form.student_billing_override;
          } else if (form.role === "professor") {
            payload.student_billing_override = null;
          }
          // Cor de marca do professor - null explícito limpa no banco
          if (form.role === "professor") {
            const _bc = /^#[0-9A-Fa-f]{6}$/.test(form.brand_color) ? form.brand_color : null;
            payload.brand_color = _bc;

            const _bgd = /^#[0-9A-Fa-f]{6}$/.test(form.brand_bg_color_dark) ? form.brand_bg_color_dark : null;
            const _bgl = /^#[0-9A-Fa-f]{6}$/.test(form.brand_bg_color_light) ? form.brand_bg_color_light : null;
            payload.brand_bg_color_dark = _bgd;
            payload.brand_bg_color_light = _bgl;
          }
        }

        if (currentUser.role === "professor") {
          payload.role = "aluno";
          payload.professor_id = currentUser.id;
          if (form.weekly_goal !== "") {
            const n = Number(form.weekly_goal);
            if (!Number.isNaN(n)) payload.weekly_goal = n;
          }
          payload.plan_expires_at = form.plan_expires_at || null;
        }

        const ok = await runAction(
          () => handleUpdateUser(editingUser.id, payload),
          "Usuário atualizado com sucesso."
        );

        if (ok) {
          resetUserForm();
          setTab(currentUser.role === "aluno" || currentUser.role === "google" ? "profile" : "list");
        }

        return;
      }

      if (!isPasswordStrong(form.password)) {
        toast.error("Senha precisa ter pelo menos 8 caracteres, 1 maiúscula, 1 número e 1 caractere especial.");
        return;
      }

      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        role: currentUser.role === "professor" ? "aluno" : form.role,
      };

      if (currentUser.role !== "aluno" && form.weekly_goal !== "") {
        const n = Number(form.weekly_goal);
        if (!Number.isNaN(n)) payload.weekly_goal = n;
      }

      // Admin criando professor com "Academia": ele nasce dono da academia
      if (currentUser.role === "admin" && payload.role === "professor" && form.organization_name?.trim()) {
        payload.organization_name = form.organization_name.trim();
      }

      const successMessage =
        currentUser.role === "professor" ? "Aluno criado com sucesso." : "Usuário criado com sucesso.";

      const ok = await runAction(() => handleCreateUser(payload), successMessage);

      if (ok) {
        resetUserForm();
        setTab(currentUser.role === "aluno" || currentUser.role === "google" ? "profile" : "list");
      }
    },
    [
      currentUser,
      editingUser,
      form,
      runAction,
      handleCreateUser,
      handleUpdateUser,
      resetUserForm,
      toast,
    ]
  );

  const handleProfileSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const formData = new FormData();
      formData.append("name", profileForm.name);
      if (profileForm.photo) {
        formData.append("photo", profileForm.photo);
      }
      if (currentUser?.role === "professor") {
        // multipart não transmite campo vazio - sentinela "__clear__" limpa no banco
        const _bc = /^#[0-9A-Fa-f]{6}$/.test(profileForm.brand_color) ? profileForm.brand_color : "";
        formData.append("brand_color", _bc || "__clear__");

        const _bgd = /^#[0-9A-Fa-f]{6}$/.test(profileForm.brand_bg_color_dark) ? profileForm.brand_bg_color_dark : "";
        formData.append("brand_bg_color_dark", _bgd || "__clear__");
        const _bgl = /^#[0-9A-Fa-f]{6}$/.test(profileForm.brand_bg_color_light) ? profileForm.brand_bg_color_light : "";
        formData.append("brand_bg_color_light", _bgl || "__clear__");
      }

      await runAction(() => handleUpdateOwnProfile(formData), "Perfil atualizado com sucesso.");
    },
    [
      profileForm.name, profileForm.photo, profileForm.brand_color,
      profileForm.brand_bg_color_dark, profileForm.brand_bg_color_light,
      currentUser?.role, runAction, handleUpdateOwnProfile,
    ]
  );

  // Nome mantido por compatibilidade (só cobria Google originalmente), mas
  // vale pra qualquer conta OAuth-only sem senha própria — Apple incluída.
  const isGoogleOnly = currentUser?.auth_provider === "google" || currentUser?.auth_provider === "apple";

  const handlePasswordSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      resetFeedback();
  
      if (!passwordForm.new_password || !passwordForm.confirm_password) {
        setError("Preencha os campos da nova senha.");
        return;
      }

      if (!isPasswordStrong(passwordForm.new_password)) {
        setError("A nova senha precisa ter pelo menos 8 caracteres, 1 maiúscula, 1 número e 1 caractere especial.");
        return;
      }
  
      if (!isGoogleOnly && !passwordForm.current_password) {
        setError("Informe a senha atual.");
        return;
      }
  
      if (passwordForm.new_password !== passwordForm.confirm_password) {
        setError("A confirmação da nova senha não confere.");
        return;
      }
  
      if (!isGoogleOnly && passwordForm.current_password === passwordForm.new_password) {
        setError("A nova senha deve ser diferente da senha atual.");
        return;
      }
  
      const payload = {
        current_password: isGoogleOnly ? "" : passwordForm.current_password,
        new_password: passwordForm.new_password,
        confirm_password: passwordForm.confirm_password,
      };
  
      const ok = await runAction(
        () => handleChangeMyPassword(payload),
        isGoogleOnly
          ? "Senha criada com sucesso. Agora você também pode entrar com email e senha."
          : "Senha alterada com sucesso."
      );
  
      if (ok) {
        resetPasswordForm();
        setCurrentUser((prev) =>
          prev ? { ...prev, auth_provider: isGoogleOnly ? "hybrid" : prev.auth_provider } : prev
        );
      }
    },
    [passwordForm, runAction, handleChangeMyPassword, isGoogleOnly]
  );

  const handleAdminPasswordSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      resetFeedback();

      if (!adminPwForm.new_password || !adminPwForm.confirm_password) {
        setError("Preencha os dois campos de senha.");
        return;
      }
      if (adminPwForm.new_password.length < 8) {
        setError("A senha deve ter pelo menos 8 caracteres.");
        return;
      }
      if (adminPwForm.new_password !== adminPwForm.confirm_password) {
        setError("A confirmação da senha não confere.");
        return;
      }

      const isTargetGoogle = editingUser?.auth_provider === "google" || editingUser?.auth_provider === "apple";
      const ok = await runAction(
        () => handleUpdateUser(editingUser.id, { password: adminPwForm.new_password }),
        isTargetGoogle
          ? "Senha criada com sucesso. O usuário já pode acessar com email e senha."
          : "Senha redefinida com sucesso."
      );

      if (ok) {
        setAdminPwForm({ new_password: "", confirm_password: "" });
        // Atualiza o editingUser local para refletir hybrid
        if (isTargetGoogle) {
          setEditingUser((prev) => prev ? { ...prev, auth_provider: "hybrid" } : prev);
        }
      }
    },
    [adminPwForm, editingUser, runAction, handleUpdateUser]
  );

  const onProfilePhotoChange = useCallback((file) => {
    if (!file) {
      setProfileForm((prev) => ({ ...prev, photo: null, preview: "" }));
      return;
    }

    const preview = URL.createObjectURL(file);
    setProfileForm((prev) => ({
      ...prev,
      photo: file,
      preview,
    }));
  }, []);

  // Contexto pra reduzir prop drilling nos componentes extraídos de
  // UserAdminModal.jsx (mesmo padrão de WorkoutAdminContext — ver
  // frontend/src/context/UserAdminContext.js, issue 012 docs/07-issues.md).
  // Migração feita um componente por vez: só o necessário pra UsersListTab
  // por enquanto, cresce conforme as próximas abas (form/profile) migrarem.
  const userAdminContextValue = useMemo(() => ({
    currentUser,
    apiBase,
    searchQuery,
    setSearchQuery,
    visibleUsers,
    usersHasMore: usersPage.hasMore,
    usersRemaining: Math.max(usersPage.total - users.length, 0),
    loadMoreUsers,
    loadingMoreUsers,
    saving,
    startEdit,
    onClickDelete,
    runAction,
    handleUpdateUser,
    setConfirmMessage,
    setConfirmLabel,
    confirmActionRef,
    setShowConfirm,
    userLabels,
    // -- usados por BrandingSection.jsx (issue 012) --
    logoFile, logoPreview, logoSaving,
    handleLogoFileChange, handleLogoUpload, handleLogoRemove,
    logoLightFile, logoLightPreview, logoLightSaving,
    handleLogoLightFileChange, handleLogoLightUpload, handleLogoLightRemove,
    appIconFile, appIconPreview, appIconSaving,
    handleAppIconFileChange, handleAppIconUpload, handleAppIconRemove,
    // -- usados por UserFormTab.jsx --
    editingUser,
    form,
    setForm,
    handleChange,
    handleSubmit,
    allowedCreateRoles,
    professors,
    resetUserForm,
    adminPwForm,
    setAdminPwForm,
    handleAdminPasswordSubmit,
    // -- usados por MyProfileTab.jsx --
    profileForm,
    setProfileForm,
    handleProfileSubmit,
    onProfilePhotoChange,
    passwordForm,
    setPasswordForm,
    handlePasswordSubmit,
    isGoogleOnly,
  }), [
    currentUser, apiBase, searchQuery, visibleUsers, saving,
    usersPage.hasMore, usersPage.total, users.length, loadMoreUsers, loadingMoreUsers,
    startEdit, onClickDelete, runAction, handleUpdateUser, userLabels,
    logoFile, logoPreview, logoSaving,
    handleLogoFileChange, handleLogoUpload, handleLogoRemove,
    logoLightFile, logoLightPreview, logoLightSaving,
    handleLogoLightFileChange, handleLogoLightUpload, handleLogoLightRemove,
    appIconFile, appIconPreview, appIconSaving,
    handleAppIconFileChange, handleAppIconUpload, handleAppIconRemove,
    editingUser, form, setForm, handleChange, handleSubmit,
    allowedCreateRoles, professors, resetUserForm,
    adminPwForm, setAdminPwForm, handleAdminPasswordSubmit,
    profileForm, setProfileForm, handleProfileSubmit, onProfilePhotoChange,
    passwordForm, setPasswordForm, handlePasswordSubmit, isGoogleOnly,
  ]);

  if (!open) return null;

  const modalContent = (
    <div className={styles.uamOverlay} onClick={onClose}>
      <div
        className={styles.uamModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="uam-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader onClose={onClose} />
        <div className={styles.uamHeader}>
          <div>
            <h2 id="uam-title">
            {userLabels.title}
            </h2>
            <p>{modalDescription}</p>
          </div>
        </div>

        <div className={styles.uamTabs}>
          {currentUser?.role !== "aluno" && currentUser?.role !== "google" && currentUser && (
            <>
              <button
                type="button"
                className={`${styles.uamTab} ${tab === "list" ? styles.active : ""}`}
                onClick={() => setTab("list")}
              >
                Lista
              </button>

              <button
                type="button"
                className={`${styles.uamTab} ${tab === "form" ? styles.active : ""}`}
                onClick={startCreate}
              >
                {userLabels.button}
              </button>
            </>
          )}

          {currentUser && currentUser?.role !== "aluno" && currentUser?.role !== "google" && (
            <button
              type="button"
              className={`${styles.uamTab} ${tab === "profile" ? styles.active : ""}`}
              onClick={() => setTab("profile")}
            >
              Meu perfil
            </button>
          )}

          {currentUser?.role === "professor" && (
            <button
              type="button"
              className={styles.uamTab}
              onClick={() => setShowInvite(true)}
              style={currentUser?.own_brand_color ? {
                background: "var(--blue)",
                color: "var(--on-accent)",
                borderColor: "var(--blue)",
                opacity: 1,
              } : undefined}
            >
              Adicionar
            </button>
          )}
        </div>

        <div className={styles.uamBody}>
          <div className={styles.uamContent}>
            {loading && <div className={styles.uamNote}>Carregando dados...</div>}

            {!loading && error && (
              <div className={`${styles.uamAlert} ${styles.error}`}>{error}</div>
            )}

            {!loading && success && (
              <div className={`${styles.uamAlert} ${styles.success}`}>{success}</div>
            )}

            {!loading && currentUser && tab === "list" && currentUser.role !== "aluno" && currentUser.role !== "google" && (
              <UsersListTab />
            )}

            {!loading && currentUser && tab === "form" && (
              <UserFormTab />
            )}

            {!loading && currentUser && tab === "profile" && (
              <MyProfileTab />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <UserAdminContext.Provider value={userAdminContextValue}>
      {modalContent}
      <InviteStudentModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        professorLogoUrl={
          currentUser?.role === "professor" && currentUser?.custom_logo_url
            ? buildImageSrc(currentUser.custom_logo_url, apiBase)
            : null
        }
        professorBrandColor={currentUser?.own_brand_color || null}
        professorName={currentUser?.name || ""}
      />
      <ConfirmDialog
        open={showConfirm}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        onClose={() => setShowConfirm(false)}
        onConfirm={async () => {
          setShowConfirm(false);
          try {
            if (confirmActionRef.current) await confirmActionRef.current();
          } catch (err) {
            // handled by runAction
          }
        }}
      />
    </UserAdminContext.Provider>,
    document.body
  );
}
